import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  lookupCardByBarcode,
  removeCards,
  searchCardProducts,
  updateCardPrice,
} from "../../api/adminInventory";
import { useAuth } from "../AuthProvider";
import { isAdminUser } from "../../helper/admin";
import { buildCardQrUrl } from "../../helper/cardScan";
import { CDN_BASE_URL, handleImageError, resolveImageUrl } from "../../helper/image";
import TransactionHistory from "./TransactionHistory";
import TaxReceipts from "./TaxReceipts";
import "../../styles/admin.css";

const CONDITIONS = ["NM", "LP", "MP", "HP", "DMG"];
const TAB_INVENTORY = "inventory";
const TAB_TRANSACTIONS = "transactions";
const TAB_TAX_RECEIPTS = "tax-receipts";

const TAB_SUBTITLES = {
  [TAB_INVENTORY]: "Look up a Card ID or search by product name to remove stock or update prices.",
  [TAB_TRANSACTIONS]: "Browse production transactions and their line items.",
  [TAB_TAX_RECEIPTS]: "Create a GST tax invoice and email it to a customer.",
};

function centsToDisplay(cents) {
  return (Number(cents) / 100).toFixed(2);
}

function displayToCents(value) {
  return Math.round(parseFloat(value || 0) * 100);
}

function formatLocation(location) {
  return `${location.binder_name} p${location.page} r${location.row} c${location.column}${
    location.is_topload ? " (topload)" : ""
  }`;
}

function getCardImageUrl(setId) {
  return setId ? resolveImageUrl(`${CDN_BASE_URL}/imgs/${setId}-low.png`) : "/DEFAULT.PNG";
}

export default function AdminTools({ initialCardId }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { cardId: routeCardId } = useParams();
  const deepLinkCardId = initialCardId ?? routeCardId;
  const barcodeRef = useRef(null);
  const resultsRef = useRef(null);

  const [barcode, setBarcode] = useState("");
  const [card, setCard] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productQuery, setProductQuery] = useState("");
  const [productResults, setProductResults] = useState([]);
  const [searchingProducts, setSearchingProducts] = useState(false);
  const [productSearchError, setProductSearchError] = useState("");
  const [chosenCards, setChosenCards] = useState([]);
  const [activeTab, setActiveTab] = useState(TAB_INVENTORY);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);

  const [priceCents, setPriceCents] = useState("");
  const [priceCondition, setPriceCondition] = useState("NM");
  const [priceVariant, setPriceVariant] = useState("");
  const activeProduct = card || selectedProduct;
  const chosenCardIds = new Set(chosenCards.map((chosenCard) => chosenCard.card_id));
  const chosenTotalCents = chosenCards.reduce(
    (total, chosenCard) => total + Number(chosenCard.price_cents || 0),
    0
  );

  useEffect(() => {
    barcodeRef.current?.focus();
  }, []);

  useEffect(() => {
    if (activeProduct) {
      setPriceCents(centsToDisplay(activeProduct.nm_price_cents));
      setPriceCondition(activeProduct.condition || "NM");
      setPriceVariant(activeProduct.variant || "");
    }
  }, [activeProduct]);

  useEffect(() => {
    const query = productQuery.trim();
    if (query.length < 2) {
      setProductResults([]);
      setProductSearchError("");
      setSearchingProducts(false);
      return undefined;
    }

    let active = true;
    setSearchingProducts(true);
    setProductSearchError("");
    const timer = setTimeout(async () => {
      try {
        const results = await searchCardProducts(query);
        if (active) setProductResults(results);
      } catch (err) {
        if (active) {
          setProductResults([]);
          setProductSearchError(err.message);
        }
      } finally {
        if (active) setSearchingProducts(false);
      }
    }, 250);

    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [productQuery]);

  const runLookup = useCallback(async (code) => {
    const trimmed = String(code || "").trim();
    if (!trimmed) return;

    setLoading(true);
    setMessage(null);
    try {
      const data = await lookupCardByBarcode(trimmed);
      setCard(data);
      setSelectedProduct(null);
      if (data.store) {
        setChosenCards((current) => {
          if (current.some((chosenCard) => chosenCard.card_id === data.card_id)) {
            return current;
          }
          return [
            ...current,
            {
              card_id: data.card_id,
              product_id: data.product_id,
              product_name: data.product_name,
              set_id: data.set_id,
              condition: data.condition,
              variant: data.variant,
              price_cents: data.price_cents,
              location: data.store,
            },
          ];
        });
      }
      setMessage({
        type: data.store ? "success" : "error",
        text: data.store
          ? `Chosen: ${data.product_name} (#${data.card_id})`
          : `${data.product_name} has no binder location`,
      });
      navigate(`/card/${data.card_id}`, { replace: true });
      setTimeout(() => resultsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
    } catch (err) {
      setCard(null);
      setSelectedProduct(null);
      setMessage({ type: "error", text: err.message });
    } finally {
      setLoading(false);
      setBarcode("");
      barcodeRef.current?.focus();
    }
  }, [navigate]);

  useEffect(() => {
    if (!deepLinkCardId || !isAdminUser(user)) return;
    setActiveTab(TAB_INVENTORY);
    runLookup(deepLinkCardId);
    // Only re-run when the URL card id or auth changes (not when runLookup is recreated)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepLinkCardId, user]);

  async function handleLookup(e) {
    e?.preventDefault();
    await runLookup(barcode);
  }

  function selectProduct(product) {
    setCard(null);
    setSelectedProduct(product);
    setProductSearchError("");
    const location = product.locations?.find(
      (candidate) => !chosenCardIds.has(candidate.card_id)
    );
    if (!location) {
      setMessage({
        type: "error",
        text: `${product.product_name} has no more unchosen binder copies`,
      });
      return;
    }

    setChosenCards((current) => [
      ...current,
      {
        card_id: location.card_id,
        product_id: product.product_id,
        product_name: product.product_name,
        set_id: product.set_id,
        condition: location.condition,
        variant: location.variant,
        price_cents: product.price_cents,
        location,
      },
    ]);
    setProductQuery("");
    setProductResults([]);
    setMessage({
      type: "success",
      text: `Chosen: ${product.product_name} from ${formatLocation(location)}`,
    });
  }

  function handleProductSearchSubmit(e) {
    e.preventDefault();
    if (productResults.length) selectProduct(productResults[0]);
  }

  if (!isAdminUser(user)) {
    return (
      <div className="admin-container hide-scrollbar">
        <div className="admin-tools">
          <h1>Access denied</h1>
          <p className="admin-subtitle">Admin or test account required.</p>
        </div>
      </div>
    );
  }

  async function handleRemoveChosen() {
    if (!chosenCards.length) return;
    const confirmed = window.confirm(
      `Remove all ${chosenCards.length} chosen card(s) from inventory?`
    );
    if (!confirmed) return;

    setLoading(true);
    setMessage(null);
    try {
      const result = await removeCards(
        chosenCards.map((chosenCard) => chosenCard.card_id)
      );
      setChosenCards([]);
      setCard(null);
      setSelectedProduct(null);
      setMessage({
        type: "success",
        text: result.detail,
      });
    } catch (err) {
      setMessage({ type: "error", text: err.message });
    } finally {
      setLoading(false);
      barcodeRef.current?.focus();
    }
  }

  function unchooseCard(cardId) {
    setChosenCards((current) =>
      current.filter((chosenCard) => chosenCard.card_id !== cardId)
    );
  }

  async function handleUpdatePrice(e) {
    e.preventDefault();
    if (!activeProduct) return;

    setLoading(true);
    setMessage(null);
    try {
      await updateCardPrice({
        productId: activeProduct.product_id,
        condition: priceCondition,
        variant: priceVariant,
        priceCents: displayToCents(priceCents),
      });
      const updatedPriceCents = displayToCents(priceCents);
      setChosenCards((current) =>
        current.map((chosenCard) =>
          chosenCard.product_id === activeProduct.product_id
            ? { ...chosenCard, price_cents: updatedPriceCents }
            : chosenCard
        )
      );
      setMessage({ type: "success", text: "Price updated" });
      if (card) {
        const refreshed = await lookupCardByBarcode(card.card_id);
        setCard(refreshed);
      } else {
        setSelectedProduct((product) => ({
          ...product,
          nm_price_cents: displayToCents(priceCents),
        }));
      }
    } catch (err) {
      setMessage({ type: "error", text: err.message });
    } finally {
      setLoading(false);
      barcodeRef.current?.focus();
    }
  }

  return (
    <div className="admin-container hide-scrollbar">
    <div className="admin-tools">
      <h1>Inventory Admin</h1>
      <p className="admin-subtitle">
        {TAB_SUBTITLES[activeTab]}
      </p>

      <div className="admin-tabs" role="tablist" aria-label="Inventory Admin sections">
        <button
          type="button"
          role="tab"
          id="admin-tab-inventory"
          aria-selected={activeTab === TAB_INVENTORY}
          aria-controls="admin-panel-inventory"
          className={`admin-tab ${activeTab === TAB_INVENTORY ? "is-active" : ""}`}
          onClick={() => setActiveTab(TAB_INVENTORY)}
        >
          Inventory
        </button>
        <button
          type="button"
          role="tab"
          id="admin-tab-transactions"
          aria-selected={activeTab === TAB_TRANSACTIONS}
          aria-controls="admin-panel-transactions"
          className={`admin-tab ${activeTab === TAB_TRANSACTIONS ? "is-active" : ""}`}
          onClick={() => setActiveTab(TAB_TRANSACTIONS)}
        >
          Transactions
        </button>
        <button
          type="button"
          role="tab"
          id="admin-tab-tax-receipts"
          aria-selected={activeTab === TAB_TAX_RECEIPTS}
          aria-controls="admin-panel-tax-receipts"
          className={`admin-tab ${activeTab === TAB_TAX_RECEIPTS ? "is-active" : ""}`}
          onClick={() => setActiveTab(TAB_TAX_RECEIPTS)}
        >
          Tax receipts
        </button>
      </div>

      {activeTab === TAB_TRANSACTIONS ? (
        <div
          role="tabpanel"
          id="admin-panel-transactions"
          aria-labelledby="admin-tab-transactions"
        >
          <TransactionHistory />
          <div className="padding-bottom" />
        </div>
      ) : activeTab === TAB_TAX_RECEIPTS ? (
        <div
          role="tabpanel"
          id="admin-panel-tax-receipts"
          aria-labelledby="admin-tab-tax-receipts"
        >
          <TaxReceipts />
          <div className="padding-bottom" />
        </div>
      ) : (
      <div
        role="tabpanel"
        id="admin-panel-inventory"
        aria-labelledby="admin-tab-inventory"
      >

      <form className="admin-scan-row" onSubmit={handleLookup}>
        <input
          ref={barcodeRef}
          type="text"
          inputMode="numeric"
          placeholder="Card ID (e.g. 901)"
          value={barcode}
          onChange={(e) => setBarcode(e.target.value)}
          disabled={loading}
          autoComplete="off"
        />
        <button type="submit" disabled={loading || !barcode.trim()}>
          {loading ? "…" : "Lookup"}
        </button>
      </form>

      <p className="admin-hint">
        Open a link like <strong>/card/905</strong>, or type an ID and tap <strong>Lookup</strong>.
      </p>

      <form className="admin-product-search" onSubmit={handleProductSearchSubmit}>
        <input
          type="search"
          placeholder="Search card products by name"
          value={productQuery}
          onChange={(e) => setProductQuery(e.target.value)}
          autoComplete="off"
          aria-label="Search card products"
        />
        {productQuery.trim().length >= 2 && (
          <div className="admin-search-results">
            {searchingProducts && <p className="admin-search-state">Searching…</p>}
            {!searchingProducts && productSearchError && (
              <p className="admin-search-state error">{productSearchError}</p>
            )}
            {!searchingProducts && !productSearchError && productResults.length === 0 && (
              <p className="admin-search-state">No matches</p>
            )}
            {!searchingProducts && productResults.map((product) => (
              <button
                type="button"
                className="admin-search-result"
                key={product.card_id || `product-${product.product_id}`}
                onClick={() => selectProduct(product)}
              >
                <span>{product.product_name}</span>
                <small>
                  {product.condition}
                  {product.variant ? ` (${product.variant})` : ""} ·{" "}
                  {product.set_name || product.set_id || "Unknown set"} · $
                  {centsToDisplay(product.price_cents)}
                </small>
                <small className={product.locations?.length ? "has-location" : ""}>
                  {product.locations?.length
                    ? product.locations.map(formatLocation).join(" · ")
                    : "No binder location"}
                </small>
                {chosenCards.some(
                  (chosenCard) => chosenCard.card_id === product.card_id
                ) && (
                  <small className="is-chosen">Chosen</small>
                )}
              </button>
            ))}
          </div>
        )}
      </form>

      {message && (
        <p className={`admin-message ${message.type}`}>{message.text}</p>
      )}

      {chosenCards.length > 0 && (
        <section className="admin-section admin-chosen-section">
          <h3>Chosen cards ({chosenCards.length})</h3>
          <ul className="admin-chosen-list">
            {chosenCards.map((chosenCard) => (
              <li key={chosenCard.card_id}>
                <img
                  src={getCardImageUrl(chosenCard.set_id)}
                  alt=""
                  onError={handleImageError}
                />
                <div>
                  <strong>{chosenCard.product_name}</strong>
                  <small>
                    {formatLocation(chosenCard.location)} · {chosenCard.condition}
                    {chosenCard.variant ? ` (${chosenCard.variant})` : ""} · $
                    {centsToDisplay(chosenCard.price_cents)}
                  </small>
                </div>
                <button
                  type="button"
                  aria-label={`Unchoose ${chosenCard.product_name}`}
                  onClick={() => unchooseCard(chosenCard.card_id)}
                  disabled={loading}
                >
                  ×
                </button>
              </li>
            ))}
          </ul>
          <p className="admin-chosen-total">
            Total: ${centsToDisplay(chosenTotalCents)}
          </p>
          <button
            type="button"
            className="admin-action-btn danger"
            disabled={loading}
            onClick={handleRemoveChosen}
          >
            {loading
              ? "Removing…"
              : `Remove all chosen (${chosenCards.length})`}
          </button>
        </section>
      )}

      {!activeProduct && !loading && !message && (
        <p className="admin-empty">No card loaded yet. Enter a Card ID or search by product name above.</p>
      )}

      {activeProduct && (
        <>
          <div className="admin-card-panel" ref={resultsRef}>
            <h2>{activeProduct.product_name}</h2>
            <div className="admin-card-content">
              <img
                className="admin-card-image"
                src={getCardImageUrl(activeProduct.set_id)}
                alt={activeProduct.product_name}
                onError={handleImageError}
              />
              <dl>
              {card && (
                <>
                  <dt>Card ID</dt>
                  <dd>{card.barcode}</dd>
                  <dt>QR link (for stickers)</dt>
                  <dd className="admin-qr-url">{buildCardQrUrl(card.card_id)}</dd>
                </>
              )}
              <dt>Product ID</dt>
              <dd>{activeProduct.product_id}</dd>
              {card && (
                <>
                  <dt>Condition</dt>
                  <dd>{card.condition} {card.variant ? `(${card.variant})` : ""}</dd>
                </>
              )}
              <dt>Set</dt>
              <dd>{activeProduct.set_name || activeProduct.set_id || "Unknown"}</dd>
              <dt>NM price</dt>
              <dd>${centsToDisplay(activeProduct.nm_price_cents)}</dd>
              <dt>{card ? "NM qty / Product qty" : "Product qty"}</dt>
              <dd>{card ? `${card.nm_quantity} / ${card.product_quantity}` : activeProduct.product_quantity}</dd>
              {card?.store && (
                <>
                  <dt>Location</dt>
                  <dd>
                    {formatLocation(card.store)}
                  </dd>
                </>
              )}
              {!card && (
                <>
                  <dt>Locations</dt>
                  <dd>
                    {activeProduct.locations?.length ? (
                      <ul className="admin-location-list">
                        {activeProduct.locations.map((location) => (
                          <li key={location.card_id}>
                            {formatLocation(location)} · {location.condition}
                            {location.variant ? ` (${location.variant})` : ""}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      "No binder location"
                    )}
                  </dd>
                </>
              )}
              </dl>
            </div>
          </div>

          <form className="admin-section" onSubmit={handleUpdatePrice}>
            <h3>Update price</h3>
            <div className="admin-form-grid">
              <label>
                NM price (AUD)
                <input
                  type="number"
                  min={0}
                  step={0.01}
                  value={priceCents}
                  onChange={(e) => setPriceCents(e.target.value)}
                />
              </label>
              <label>
                Condition
                <select value={priceCondition} onChange={(e) => setPriceCondition(e.target.value)}>
                  {CONDITIONS.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </label>
              <label className="full-width">
                Variant
                <input value={priceVariant} onChange={(e) => setPriceVariant(e.target.value)} />
              </label>
            </div>
            <button type="submit" className="admin-action-btn secondary" disabled={loading}>
              Update price
            </button>
          </form>
        </>
      )}
      <div className="padding-bottom" />
      </div>
      )}
    </div>
    </div>
  );
}
